<template>
  <div class="dashboard">
    <!-- SIDEBAR -->
    <aside class="a">
      <div class="brand">
        <div class="logo">!</div>
        <span>{{ $t('newsletterCard.titleNews') }}</span>
      </div>

      <nav class="nav-section">
        <h4>{{ $t('pages.categories') }}</h4>
        <ul class="nav-list">
          <!-- All Articles -->
          <li class="nav-item">
            <button
              class="category-btn"
              :class="{ active: selectedCategory === null }"
              @click="selectCategory(null)"
            >
              <span class="cat-name">{{ $t('newsletterCard.allArticles') }}</span>
              <span class="cat-count">{{ posts.length }}</span>
            </button>
          </li>

          <!-- Dynamic Categories -->
          <li v-for="cat in rootCategories" :key="cat.id" class="nav-item">
            <button
              class="category-btn"
              :class="{ active: selectedCategory === cat.id }"
              @click="selectCategory(cat.id)"
            >
              <span class="cat-name">{{ cat.name }}</span>
              <span class="cat-count">{{ getCategoryCount(cat.id) }}</span>
            </button>

            <!-- Subcategories -->
            <ul v-if="getChildren(cat.id).length" class="sub-nav">
              <li v-for="child in getChildren(cat.id)" :key="child.id">
                <button
                  class="subcategory-btn"
                  :class="{ active: selectedCategory === child.id }"
                  @click.stop="selectCategory(child.id)"
                >
                  {{ child.name }}
                </button>
              </li>
            </ul>
          </li>
        </ul>
      </nav>

      <div class="sidebar-footer">
        <p>© {{ currentYear }} TapTasty</p>
      </div>
    </aside>

    <!-- MAIN CONTENT -->
    <main class="main-content">

      <!-- LIST VIEW -->
      <div v-if="!selectedPost" class="list-view">
        <header class="page-header">
          <div class="header-content">
            <h1>{{ currentCategoryName }}</h1>
            <p class="subtitle">{{ filteredPosts.length }} {{ $t('newsletterCard.articleFound') }}</p>
          </div>
          <div class="header-actions">
            <div class="search-box">
              <div class="search-inner">
                <svg class="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
                <input
                  v-model="searchQuery"
                  type="text"
                    :placeholder="$t('newsletterCard.searchArticle')"
                />
              </div>
            </div>
          </div>
        </header>

        <div class="articles-grid">
          <article
            v-for="post in filteredPosts"
            :key="post.id"
            class="article-card"
            @click="openPost(post)"
          >
            <div class="card-image-wrapper">
              <img :src="post.featured_image" :alt="getTitle(post)" />
              <div class="card-overlay">
                <span class="read-more">{{ $t('newsletterCard.readArticle') }}</span>
              </div>
            </div>

            <div class="card-content">
              <div class="card-meta">
                <span class="date">{{ formatDate(post.date) }}</span>
                <span class="dot">•</span>
                <span class="read-time">5 min read</span>
              </div>
              <h3>{{ getTitle(post) }}</h3>
              <p>{{ getExcerpt(post) }}</p>
            </div>
          </article>
        </div>
      </div>

      <!-- DETAIL VIEW -->
      <article v-else class="detail-view">
        <button class="back-btn" @click="selectedPost = null">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" width="16" height="16">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to articles
        </button>

        <header class="article-header">
          <h1>{{ getTitle(selectedPost) }}</h1>
          <div class="article-meta">
            <span class="published">Published {{ formatDate(selectedPost.date) }}</span>
            <span class="separator">|</span>
            <span class="author">By TapTasty Team</span>
          </div>
        </header>

        <img
          v-if="selectedPost.featured_image"
          :src="selectedPost.featured_image"
          class="hero-image"
          :alt="getTitle(selectedPost)"
        />

        <div class="article-body" v-html="getContent(selectedPost)"></div>
      </article>

    </main>
  </div>
</template>

<script>
export default {
  data () {
    return {
      categories: [],
      posts: [],
      selectedCategory: null,
      selectedPost: null,
      searchQuery: ''
    }
  },

  computed: {
    // Exclude "Fără categorie" (id: 1) din root categories
    rootCategories () {
      return this.categories.filter(c => c.parent === 0 && c.id !== 1)
    },
    currentYear () {
      return new Date().getFullYear()
    },
    filteredPosts () {
      let result = this.posts

      // CATEGORY FILTER - dinamic pentru orice categorie
      if (this.selectedCategory) {
        result = result.filter(post => {
          // Dacă post-ul are categoria selectată direct
          if (post.categories.includes(this.selectedCategory)) {
            return true
          }

          // Dacă e categorie părinte, arată toate post-urile care au
          // ORICE subcategorie a acestei categorii
          const childIds = this.getAllChildrenIds(this.selectedCategory)
          return childIds.some(childId => post.categories.includes(childId))
        })
      }

      // SEARCH FILTER
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase()
        result = result.filter(post => {
          const title = (post.title.rendered || post.title || '').toLowerCase()
          const excerpt = (post.excerpt.rendered || post.excerpt || '')
            .replace(/<[^>]+>/g, '')
            .toLowerCase()
          return title.includes(q) || excerpt.includes(q)
        })
      }

      return result
    },

    currentCategoryName () {
      if (this.selectedCategory === null) {
        return this.$t('newsletterCard.allArticles')
      }

      const cat = this.categories.find(c => c.id === this.selectedCategory)
      return cat ? cat.name : this.$t('newsletterCard.allArticles')
    }
  },

  async created () {
    await this.fetchData()
  },

  methods: {
    async fetchData () {
      try {
        const res = await fetch('https://newsletter.taptasty.com/wp-json/secure/v1/blog-data', {
          headers: {
            Authorization: 'Bearer 2dc5f2d429a3d971b7403d918b2177f65a14b8b87a4741856206c8099627ea65'
          }
        })
        const data = await res.json()
        this.categories = data.categories
        this.posts = data.posts
      } catch (err) {
        console.error('Failed to fetch data:', err)
      }
    },

    // Helper: ia toate ID-urile copiilor (recursiv)
    getAllChildrenIds (parentId) {
      const directChildren = this.categories.filter(c => c.parent === parentId)
      let allIds = directChildren.map(c => c.id)

      directChildren.forEach(child => {
        allIds = allIds.concat(this.getAllChildrenIds(child.id))
      })

      return allIds
    },

    getTitle (post) {
      return post.title.rendered || post.title || ''
    },

    getExcerpt (post) {
      return (post.excerpt.rendered || post.excerpt || '').replace(/<[^>]+>/g, '')
    },

    getContent (post) {
      return post.content.rendered || post.content || ''
    },

    selectCategory (id) {
      this.selectedCategory = id
      this.selectedPost = null
      this.searchQuery = ''
    },

    openPost (post) {
      this.selectedPost = post
    },

    getChildren (parentId) {
      return this.categories.filter(c => c.parent === parentId)
    },

    getCategoryCount (catId) {
      // Numără post-urile care au această categorie sau orice subcategorie
      const allCatIds = [catId, ...this.getAllChildrenIds(catId)]
      return this.posts.filter(p =>
        p.categories.some(cat => allCatIds.includes(cat))
      ).length
    },

    formatDate (date) {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.dashboard {
  display: flex;
  min-height: 100vh;
  background: #f8fafc;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* ── SIDEBAR ─────────────────────────────────── */
.a {
  width: 260px;
  background: #fff;
  color: #25396F;
  display: flex;
  flex-direction: column;
  position: fixed;
  height: 100vh;
  overflow-y: auto;
  z-index: 50;
  border-right: 1px solid #eee;
}

.brand {
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid #eee;
}

.logo {
  width: 40px;
  height: 40px;
  background: #674BD2;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 16px;
  color: #fff;
}

.brand span {
  font-size: 21px;
  font-weight: 700;
  color: #25396F;
  letter-spacing: -0.5px;
}

.nav-section {
  flex: 1;
  padding: 24px 16px;
}

.nav-section h4 {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #25396F;
  margin-bottom: 16px;
  padding-left: 12px;
  font-weight: 700;
}

.nav-list {
  list-style: none;
}

.nav-item {
  margin-bottom: 4px;
}

.category-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background: transparent;
  border: none;
  color: #25396F;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.category-btn:hover {
  background: rgba(37, 57, 111, 0.08);
  color: #25396F;
}

.category-btn.active {
  background: #674BD2;
  color: #fff;
  font-weight: 700;
}

.cat-count {
  font-size: 12px;
  padding: 2px 8px;
  background: rgba(37, 57, 111, 0.12);
  border-radius: 12px;
  min-width: 24px;
  text-align: center;
  color: #25396F;
  font-weight: 600;
}

.category-btn.active .cat-count {
  background: rgba(255,255,255,0.25);
  color: #fff;
}

.sub-nav {
  list-style: none;
  margin-top: 4px;
  margin-left: 12px;
  padding-left: 12px;
  border-left: 2px solid rgba(37, 57, 111, 0.15);
}

.subcategory-btn {
  width: 100%;
  text-align: left;
  padding: 8px 12px;
  background: transparent;
  border: none;
  color: #25396F;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.subcategory-btn:hover {
  background: rgba(37, 57, 111, 0.08);
  color: #25396F;
}

.subcategory-btn.active {
  color: #fff;
  background: #674BD2;
  font-weight: 600;
}

.sidebar-footer {
  padding: 16px 24px;
  border-top: 1px solid #eee;
  font-size: 12px;
  color: #25396F;
  font-weight: 600;
}

/* ── MAIN ────────────────────────────────────── */
.main-content {
  flex: 1;
  padding: 40px;
  min-height: 100vh;
  margin-left: 260px;
}

/* ── HEADER ──────────────────────────────────── */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e2e8f0;
}

.header-content h1 {
  font-size: 30px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 6px;
  letter-spacing: -0.5px;
}

.subtitle {
  color: #64748b;
  font-size: 14px;
}

/* ── SEARCH ──────────────────────────────────── */
.search-box {
  display: flex;
  justify-content: flex-end;
}

.search-inner {
  position: relative;
  display: flex;
  align-items: center;
}

.search-inner input {
  width: 240px;
  padding: 10px 14px 10px 38px;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  background: #fff;
  font-size: 14px;
  color: #1e293b;
  outline: none;
  transition: all 0.25s ease;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.search-inner input::placeholder {
  color: #94a3b8;
}

.search-inner input:focus {
  width: 280px;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99,102,241,0.12);
}

.search-icon {
  position: absolute;
  left: 12px;
  width: 16px;
  height: 16px;
  color: #94a3b8;
  pointer-events: none;
  stroke-width: 2;
}

/* ── GRID ────────────────────────────────────── */
.articles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
}

.article-card {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.25s ease;
  border: 1px solid #e2e8f0;
}

.article-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 16px 32px rgba(0,0,0,0.1);
  border-color: transparent;
}

.card-image-wrapper {
  position: relative;
  height: 200px;
  overflow: hidden;
  background: #f1f5f9;
}

.card-image-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.article-card:hover .card-image-wrapper img {
  transform: scale(1.05);
}

.card-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.65), transparent);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 20px;
  opacity: 0;
  transition: opacity 0.25s ease;
}

.article-card:hover .card-overlay {
  opacity: 1;
}

.read-more {
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  padding: 7px 18px;
  border-radius: 100px;
  border: 1px solid rgba(255,255,255,0.35);
  backdrop-filter: blur(4px);
}

.card-content {
  padding: 20px 24px 24px;
}

.card-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 10px;
}

.card-content h3 {
  font-size: 17px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 10px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-content p {
  font-size: 14px;
  color: #64748b;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ── DETAIL VIEW ─────────────────────────────── */
.detail-view {
  max-width: 1220px;
  margin: 0 auto;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  margin-bottom: 32px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #64748b;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.back-btn:hover {
  border-color: #6366f1;
  color: #6366f1;
}

.article-header h1 {
  font-size: 38px;
  font-weight: 800;
  color: #1e293b;
}

.article-meta {
  display: flex;
  gap: 10px;
  color: #64748b;
  font-size: 14px;
}

.hero-image {
  width: 100%;
  height: 400px;
  object-fit: cover;
  border-radius: 16px;
  margin-bottom: 40px;
}

.article-body {
  font-size: 17px;
  line-height: 1.85;
  color: #334155;
}

/* ── RESPONSIVE ──────────────────────────────── */
@media (max-width: 1024px) {
  .main-content {
    margin-left: 220px;
    padding: 28px;
  }
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding: 20px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .search-inner input {
    width: 100%;
  }

  .articles-grid {
    grid-template-columns: 1fr;
  }

  .article-header h1 {
    font-size: 26px;
  }

  .hero-image {
    height: 220px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Newsletter/Tabs/News.vue